import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Sparkles } from "lucide-react";
import { api, API, TOKEN_KEY } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const SESSION_KEY = "vaya_chat_session";

export default function Support() {
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [consent, setConsent] = useState(localStorage.getItem("vaya_ai_consent") === "1");
  const endRef = useRef(null);
  const sessionRef = useRef(localStorage.getItem(SESSION_KEY) || "");

  useEffect(() => {
    if (!sessionRef.current) return;
    api
      .get("/ai/chat/history", { params: { session_id: sessionRef.current } })
      .then(({ data }) =>
        setMessages(
          (data.messages || []).map((m, i) => ({
            id: m.id || `hist-${i}`,
            role: m.role === "user" ? "user" : "assistant",
            content: m.content || m.message || m.text || "",
          }))
        )
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const message = text.trim();
    if (!message || sending) return;
    if (!consent) return toast.error(t("support.consentRequired"));
    setText("");
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "user", content: message }, { id: crypto.randomUUID(), role: "assistant", content: "" }]);
    setSending(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}`,
        },
        body: JSON.stringify({ message, session_id: sessionRef.current || null, ai_consent: true }),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try {
          const j = await res.json();
          if (j?.detail) detail = typeof j.detail === "string" ? j.detail : JSON.stringify(j.detail);
        } catch { /* keep status */ }
        throw new Error(detail);
      }
      if (!res.body) throw new Error(t("common.error"));

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let reply = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let payload;
          try {
            payload = JSON.parse(line.slice(5).trim());
          } catch {
            continue;
          }
          if (payload.session_id) {
            sessionRef.current = payload.session_id;
            localStorage.setItem(SESSION_KEY, payload.session_id);
          }
          if (payload.delta) {
            reply += payload.delta;
            setMessages((m) => {
              const next = [...m];
              next[next.length - 1] = { ...next[next.length - 1], content: reply };
              return next;
            });
          }
        }
      }
      if (!reply)
        setMessages((m) => {
          const next = [...m];
          next[next.length - 1] = { ...next[next.length - 1], content: t("common.error") };
          return next;
        });
    } catch (err) {
      setMessages((m) => m.slice(0, -1));
      toast.error(err.message || t("common.error"));
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-3xl px-5 py-12 lg:py-16">
        <p className="label-eyebrow">Vaya AI</p>
        <h1 className="mt-2 font-display text-3xl tracking-tight sm:text-4xl" data-testid="support-title">
          {t("support.title")}
        </h1>
        <p className="mt-3 text-base text-slate-600">{t("support.sub")}</p>

        <div
          className="mt-8 min-h-[380px] space-y-4 rounded-xl border border-slate-200 bg-white p-6 ambient"
          data-testid="chat-window"
        >
          {messages.length === 0 && (
            <p className="flex items-center gap-2 text-sm text-slate-500">
              <Sparkles size={15} className="text-gold" /> {t("support.empty")}
            </p>
          )}
          {messages.map((m, i) => (
            <div
              key={m.id || i}
              data-testid={`chat-message-${m.role}-${m.id || i}`}
              className={`max-w-[85%] whitespace-pre-line rounded-lg px-4 py-3 text-sm leading-relaxed ${
                m.role === "user"
                  ? "ml-auto bg-sea text-white"
                  : "border border-slate-200 bg-slate-50 text-slate-800"
              }`}
            >
              {(m.content || "").replace(/\*\*/g, "").replace(/^#+\s/gm, "") ||
                (sending ? t("common.loading") : "")}
            </div>
          ))}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="mt-5" data-testid="chat-form">
          {!consent && (
            <label className="mb-3 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600" data-testid="ai-consent">
              <input
                type="checkbox"
                data-testid="ai-consent-checkbox"
                className="mt-0.5 h-4 w-4 accent-sea"
                checked={consent}
                onChange={(e) => {
                  setConsent(e.target.checked);
                  localStorage.setItem("vaya_ai_consent", e.target.checked ? "1" : "0");
                }}
              />
              <span>{t("support.consent")}</span>
            </label>
          )}
          <div className="flex gap-3">
            <Input
              data-testid="chat-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("support.placeholder")}
              disabled={!consent}
              className="h-12"
            />
            <Button
              type="submit"
              data-testid="chat-send-btn"
              disabled={sending || !consent}
              className="h-12 bg-sea px-6 hover:bg-sea-dark"
            >
              <Send size={16} className="mr-1.5" /> {t("support.send")}
            </Button>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
}
